import './defines';
import { mobileTools } from 'tools';
import {
	Component,
	toNative,
} from 'utils/vue-facing-decorator';
import {
	Prop,
	Vue,
} from 'vue-facing-decorator';
import GoogleIcon from './assets/google.svg';
import Template from './template.vue';

@Component({
	name: 'GoogleButtonComponent',
	emits: ['click'],
	mixins: [Template],
})
class GoogleButtonComponent extends Vue {
	@Prop({
		default: false,
		description: 'Indicates if the button should be disabled',
		type: Boolean,
	})
	public readonly disabled!: boolean;

	@Prop({
		default: 'auto',
		description: 'Defines the font size of the button, when being `auto` the font size is going to be determined if the @media query is mobile or not',
		type: [
			Number,
			String,
		],
	})
	public readonly fontSize?: number | string;

	@Prop({
		default: 'auto',
		description: 'Defines the height of the button',
		type: [
			Number,
			String,
		],
	})
	public readonly height?: number | string;

	@Prop({
		default: false,
		description: 'Manually triggers the hover state of the button',
		type: Boolean,
	})
	public readonly hovered!: boolean;

	@Prop({
		default: 'auto',
		description: 'Defines the width of the button',
		type: [
			Number,
			String,
		],
	})
	public readonly width?: number | string;

	protected get computedClasses(): Record<string, boolean> {
		return {
			'google-button-component-disabled': this.disabled,
			'google-button-component-hovered': this.hovered,
		};
	}

	protected get computedStyles(): Partial<CSSStyleDeclaration> {
		const styles: Partial<CSSStyleDeclaration> = {};

		if (typeof this.height === 'number') {
			styles.height = `${this.height}px`;
		} else if (this.height?.startsWith('--')) {
			styles.height = `var(${this.height})`;
		} else if (this.height) {
			styles.height = this.height;
		}

		if (typeof this.width === 'number') {
			styles.width = `${this.width}px`;
		} else if (
			this.width?.substring(
				0,
				2,
			) === '--'
		) {
			styles.width = `var(${this.width})`;
		} else if (this.width) {
			styles.width = this.width;
		}

		if (typeof this.fontSize === 'number') {
			styles.fontSize = `${this.fontSize}px`;
		} else if (this.fontSize === 'auto') {
			let fontSize: string;

			if (this.isMobile) {
				fontSize = 'var(--font-size-xs2)';
			} else {
				fontSize = 'var(--font-size-s)';
			}

			styles.fontSize = fontSize;
		} else if (this.fontSize?.startsWith('--')) {
			styles.fontSize = `var(${this.fontSize})`;
		} else if (this.fontSize) {
			styles.fontSize = this.fontSize;
		}

		return styles;
	}

	protected googleIcon = GoogleIcon;

	private isMobile = mobileTools.getIsMobile();

	private isMobileUnwatch?: () => void;

	protected beforeUnmount(): void {
		this.isMobileUnwatch?.();
	}

	protected mounted(): void {
		this.isMobileUnwatch = mobileTools.watch(() => {
			this.isMobile = mobileTools.getIsMobile();
		});
	}

	protected onClick(event: MouseEvent): void {
		this.$emit(
			'click',
			event,
		);
	}
}

export default toNative(GoogleButtonComponent);
